"use client"

import { useEffect, useState } from "react"
import { useTheme } from "next-themes"
import { Moon, Sun, Monitor } from "lucide-react"
import { persistAppearanceMode } from "@/components/platform/appearance"

// 3-way pill (light / system / dark) mirroring ProductBrain's ThemeSwitcher.
// Uses next-themes (attribute="class"); mounted-guard prevents SSR flash. An explicit choice is
// also persisted (per-device cookie + durable identity_users.appearance) via persistAppearanceMode,
// so it survives refresh/login and carries to other devices — next-themes stays the mode authority.
export function ThemeToggle() {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  const choose = (m: "light" | "system" | "dark") => { setTheme(m); persistAppearanceMode(m) }

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      style={{
        display: "inline-flex",
        gap: 2,
        padding: 3,
        borderRadius: 999,
        border: "1px solid var(--gtm-border-soft)",
        background: "var(--gtm-surface)",
      }}
    >
      <SwitchButton value="light" active={mounted && theme === "light"} onClick={() => choose("light")}>
        <Sun size={14} />
      </SwitchButton>
      <SwitchButton value="system" active={mounted && theme === "system"} onClick={() => choose("system")}>
        <Monitor size={14} />
      </SwitchButton>
      <SwitchButton value="dark" active={mounted && theme === "dark"} onClick={() => choose("dark")}>
        <Moon size={14} />
      </SwitchButton>
    </div>
  )
}

function SwitchButton({
  value,
  active,
  onClick,
  children,
}: {
  value: string
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      aria-label={`${value} theme`}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
        height: 22,
        padding: 0,
        borderRadius: 999,
        border: "none",
        cursor: "pointer",
        background: active ? "var(--gtm-surface2)" : "transparent",
        color: active ? "var(--gtm-text)" : "var(--gtm-dim)",
        transition: "color .12s, background .12s",
      }}
    >
      {children}
    </button>
  )
}
